import { getOrCreateAnonId } from './freshchatStorage'

type AnyRecord = Record<string, unknown>

type FreshchatWidgetApi = {
  init?: (args: unknown) => unknown
  destroy?: () => unknown
  on?: (event: string, handler: (...args: unknown[]) => unknown) => unknown
  open?: () => unknown
  close?: () => unknown
  isOpen?: () => boolean
  setExternalId?: (externalId: string) => unknown
  user?: {
    setExternalId?: (externalId: string) => unknown
    setFirstName?: (firstName: string) => unknown
    setLastName?: (lastName: string) => unknown
    setEmail?: (email: string) => unknown
    setProperties?: (props: AnyRecord) => unknown
    clear?: () => Promise<unknown> | unknown
    get?: () => Promise<unknown> | unknown
  }
}

declare global {
  interface Window {
    fcWidget?: FreshchatWidgetApi
    __clsPlusFreshchatInitialized?: boolean
    __clsPlusFreshchatInitExternalId?: string | null
    __clsPlusFreshchatInitCalled?: boolean
    fcSettings?: AnyRecord
  }
}

const FRESHCHAT_SCRIPT_ID = 'freshchat-js-sdk'
const FRESHCHAT_SCRIPT_SRC = 'https://wchat.eu.freshchat.com/js/widget.js'

const FORCE_CLEAN_BOOT_KEY = 'cls_plus_freshchat_force_clean_boot'

const FRESHCHAT_STORAGE_KEY_PATTERN = /(freshchat|freshworks|fd[_-]?messaging|fcwidget|fc_widget|wchat)/i

const FRESHCHAT_IDB_NAME_PATTERN = /(freshchat|freshworks|fd[_-]?messaging|fcwidget|wchat)/i

const isWebDriver = () => {
  // Playwright/Selenium set this; we disable loading the real widget during automation
  // to avoid flakiness and external network dependencies.
  try {
    return typeof navigator !== 'undefined' && Boolean((navigator as unknown as { webdriver?: boolean }).webdriver)
  } catch {
    return false
  }
}

const isEnabled = () => {
  if (isWebDriver()) return false
  return String(import.meta.env.VITE_FRESHCHAT_ENABLED || '').toLowerCase() === 'true'
}

const getHost = () => import.meta.env.VITE_FRESHCHAT_HOST || 'https://wchat.eu.freshchat.com'
const getToken = () => import.meta.env.VITE_FRESHCHAT_TOKEN || ''

const isDevEnv = () => String(import.meta.env.VITE_ENV || '').toUpperCase() === 'DEV'
const isTestHostname = () => {
  if (typeof window === 'undefined') return false
  return window.location.hostname === 'test.clsplus.sportradar.cricket'
}

const isDiagnosticsEnabled = () => {
  const forced = String(import.meta.env.VITE_FRESHCHAT_DIAGNOSTICS_ENABLED || '').toLowerCase() === 'true'
  return forced || isDevEnv() || isTestHostname()
}

const logDiagnostics = (event: string, details: Record<string, unknown>) => {
  if (!isDiagnosticsEnabled()) return
  // eslint-disable-next-line no-console
  console.groupCollapsed(`[Freshchat][DEV][client] ${event}`)
  // eslint-disable-next-line no-console
  console.log(details)
  // eslint-disable-next-line no-console
  console.groupEnd()
}

const getTags = (): string[] => {
  const raw = import.meta.env.VITE_FRESHCHAT_TAGS || 'clsp_scout'
  return raw
    .split(',')
    .map((s: string) => s.trim())
    .filter(Boolean)
}

let initPromise: Promise<void> | null = null
let isWidgetInitialized = false
let loadPromise: Promise<void> | null = null
let lastAppliedExternalId: string | null = null
let lastAppliedProfileFieldsSignature: string | null = null
let didStartupCleanup = false
let didAttachVersionErrorListener = false

export const freshchatMarkForceCleanBoot = () => {
  try {
    window.localStorage.setItem(FORCE_CLEAN_BOOT_KEY, '1')
  } catch {
    // ignore
  }
}

const shouldClearPersistedStateOnStartup = () => {
  const forced = String(import.meta.env.VITE_FRESHCHAT_CLEAR_PERSISTED_STATE_ON_STARTUP || '').toLowerCase() === 'true'
  // Only clear when explicitly forced. Clearing by default breaks chat continuity
  // on refresh (and shouldn't be necessary once identity churn is controlled).
  let forcedByLogin = false
  try {
    forcedByLogin = window.localStorage.getItem(FORCE_CLEAN_BOOT_KEY) === '1'
  } catch {
    forcedByLogin = false
  }
  return forced || forcedByLogin
}

const maybeClearPersistedStateOnStartup = async () => {
  if (didStartupCleanup) return
  didStartupCleanup = true
  if (!shouldClearPersistedStateOnStartup()) return
  try {
    window.localStorage.removeItem(FORCE_CLEAN_BOOT_KEY)
  } catch {
    // ignore
  }

  logDiagnostics('startup:clearPersistedState', {
    forceCleanBootKey: FORCE_CLEAN_BOOT_KEY,
  })

  await freshchatClearPersistedState()
}

const maybeAttachVersionErrorListener = () => {
  if (!isDiagnosticsEnabled()) return
  if (didAttachVersionErrorListener) return
  didAttachVersionErrorListener = true

  window.addEventListener('error', event => {
    try {
      const message = String((event as unknown as { message?: unknown }).message || '')
      if (!message) return
      if (!message.includes('widget.index') && !message.includes('Missing params: version')) return

      logDiagnostics('error:widget.index', {
        message,
        initExternalId: window.__clsPlusFreshchatInitExternalId,
        initCalled: Boolean(window.__clsPlusFreshchatInitCalled),
        settingsExternalId: window.fcSettings?.externalId,
        lastAppliedExternalId,
        widgetPresent: Boolean(window.fcWidget),
        widgetInitialized: Boolean(window.__clsPlusFreshchatInitialized),
      })
    } catch {
      // ignore
    }
  })
}

// Freshchat user/identity calls are stateful and can race each other (especially around logout).
// Serialize operations to avoid interleaving clear/init/setProperties.
let userOpQueue: Promise<void> = Promise.resolve()

const enqueueUserOp = async <T>(op: () => Promise<T> | T): Promise<T | undefined> => {
  const run = async () => {
    try {
      return await op()
    } catch {
      return undefined
    }
  }

  const next = userOpQueue.then(run, run)
  userOpQueue = next.then(
    () => undefined,
    () => undefined
  )
  return next
}

const sleep = (ms: number) => new Promise<void>(resolve => window.setTimeout(resolve, ms))

const ensureSettings = (externalId: string) => {
  const token = getToken()
  if (!token) return

  // Preferred bootstrap: configure settings then load the script.
  // Keep this idempotent so callers can override externalId before init.
  window.fcSettings = {
    token,
    host: getHost(),
    tags: getTags(),
    externalId,
    config: {
      disableNotifications: true,
      agent: { hideName: true },
      cssNames: {
        widget: 'fc_button',
        open: 'fc_open',
        expanded: 'fc_expanded',
      },
    },
  }

  logDiagnostics('settings:ensure', {
    externalId,
    host: getHost(),
    hasToken: Boolean(token),
    tags: getTags(),
  })
}

// Allows callers to set the initial externalId before the widget script boots.
// This helps preserve conversations across refresh by avoiding an immediate
// post-load identity switch (which can wipe chat in some widget builds).
export const freshchatPrimeExternalId = (externalId: string) => {
  if (typeof window === 'undefined') return
  ensureSettings(externalId)
  if (window.fcSettings) window.fcSettings.externalId = externalId
  window.__clsPlusFreshchatInitExternalId = externalId

  logDiagnostics('externalId:prime', {
    externalId,
  })
}

const getWidget = () => window.fcWidget

const waitForWidget = async (timeoutMs: number = 15000) => {
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    const fcWidget = getWidget()
    if (fcWidget) return
    await new Promise<void>(resolve => window.setTimeout(resolve, 50))
  }
}

const waitForUserSurface = async (timeoutMs: number = 15000) => {
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    const fcWidget = getWidget()
    if (fcWidget?.user?.setProperties) return
    await new Promise<void>(resolve => window.setTimeout(resolve, 50))
  }
}

const waitForUserSurfaceOrNull = async (timeoutMs: number) => {
  try {
    await waitForUserSurface(timeoutMs)
    const fcWidget = getWidget()
    if (fcWidget?.user?.setProperties) return fcWidget
    return null
  } catch {
    return null
  }
}

const waitForWidgetClosed = async (timeoutMs: number = 2000) => {
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    const fcWidget = getWidget()
    const isOpen = fcWidget?.isOpen
    if (typeof isOpen === 'function') {
      try {
        if (!isOpen()) return
      } catch {
        // ignore
      }
    } else {
      // If we can't observe open/close state, don't block.
      return
    }
    await new Promise<void>(resolve => window.setTimeout(resolve, 50))
  }
}

const safeCall = async (fn: () => unknown) => {
  try {
    await fn()
  } catch {
    // no-op
  }
}

const clearStorageLike = (storage: Storage | undefined) => {
  if (!storage) return
  try {
    const keys: string[] = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (key) keys.push(key)
    }

    keys.forEach(key => {
      if (FRESHCHAT_STORAGE_KEY_PATTERN.test(key)) {
        try {
          storage.removeItem(key)
        } catch {
          // ignore
        }
      }
    })
  } catch {
    // ignore
  }
}

const deleteIndexedDbLike = async () => {
  if (typeof indexedDB === 'undefined') return

  const deleteDb = (name: string) =>
    new Promise<void>(resolve => {
      try {
        const req = indexedDB.deleteDatabase(name)
        req.onsuccess = () => resolve()
        req.onerror = () => resolve()
        req.onblocked = () => resolve()
      } catch {
        resolve()
      }
    })

  // Prefer enumeration when available (Chromium).
  const anyIndexedDb = indexedDB as unknown as { databases?: () => Promise<Array<{ name?: string | null }>> }
  if (typeof anyIndexedDb.databases === 'function') {
    try {
      const dbs = await anyIndexedDb.databases()
      const names = (dbs || [])
        .map(db => db?.name)
        .filter((name): name is string => Boolean(name))
        .filter(name => FRESHCHAT_IDB_NAME_PATTERN.test(name))

      await Promise.all(names.map(deleteDb))
      return
    } catch {
      // fall through to heuristic deletes
    }
  }

  // Heuristic deletes for browsers without indexedDB.databases().
  const commonNames = ['freshchat', 'freshworks', 'fd-messaging', 'fd_messaging', 'fcwidget', 'wchat']
  await Promise.all(commonNames.map(deleteDb))
}

export const freshchatClearPersistedState = async () => {
  // Removes persisted widget state (drafts/session) that can cause the widget
  // to crash with Ember router errors after an identity change.
  await safeCall(() => {
    clearStorageLike(window.localStorage)
    clearStorageLike(window.sessionStorage)
  })

  // Some widget builds persist router/conversation state in IndexedDB.
  // If this becomes inconsistent it can crash on reload with missing route params.
  await safeCall(async () => {
    await deleteIndexedDbLike()
  })

  // Reset globals to force a clean bootstrap.
  await safeCall(() => {
    const existing = document.getElementById(FRESHCHAT_SCRIPT_ID)
    if (existing?.parentNode) existing.parentNode.removeChild(existing)
  })

  window.__clsPlusFreshchatInitialized = false
  window.__clsPlusFreshchatInitExternalId = null
  window.__clsPlusFreshchatInitCalled = false
  initPromise = null
  loadPromise = null
  isWidgetInitialized = false
  lastAppliedExternalId = null
}

const waitForExternalId = async (expectedExternalId: string, timeoutMs: number = 800) => {
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    const fcWidget = getWidget()
    const getUser = fcWidget?.user?.get
    if (typeof getUser === 'function') {
      try {
        const user = (await getUser()) as unknown
        if (user && typeof user === 'object') {
          const externalId = (user as { externalId?: unknown }).externalId
          if (typeof externalId === 'string' && externalId === expectedExternalId) return
        }
      } catch {
        // ignore
      }
    }
    await sleep(50)
  }
}

export const ensureFreshchatLoaded = async (): Promise<void> => {
  if (!isEnabled()) return
  if (typeof window === 'undefined') return

  const token = getToken()
  if (!token) return

  // Script might already be present but the API not fully wired yet.
  if (window.fcWidget?.user?.setProperties) return
  if (loadPromise) return loadPromise

  const existing = document.getElementById(FRESHCHAT_SCRIPT_ID) as HTMLScriptElement | null
  if (existing) {
    loadPromise = new Promise<void>((resolve, reject) => {
      const timeoutMs = 15000
      const start = Date.now()

      const tick = () => {
        if (window.fcWidget?.user?.setProperties) {
          resolve()
          return
        }
        if (Date.now() - start > timeoutMs) {
          reject(new Error('Freshchat widget did not become ready in time'))
          return
        }
        window.setTimeout(tick, 50)
      }

      tick()
    })

    return loadPromise
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.id = FRESHCHAT_SCRIPT_ID
    script.async = true
    script.src = FRESHCHAT_SCRIPT_SRC
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Freshchat widget script'))
    document.head.appendChild(script)
  })

  return loadPromise
}

export const ensureFreshchatInitialized = async (preferredExternalId?: string): Promise<void> => {
  await maybeClearPersistedStateOnStartup()
  maybeAttachVersionErrorListener()

  // If the widget is already present (e.g. Playwright stubs), allow using it even when disabled.
  if (!isEnabled()) {
    if (window.fcWidget) {
      isWidgetInitialized = true
      window.__clsPlusFreshchatInitialized = true
      return
    }
    return
  }
  if (window.__clsPlusFreshchatInitialized) {
    isWidgetInitialized = true
    return
  }
  if (isWidgetInitialized) return
  if (initPromise) return initPromise

  // Ensure the very first init uses the caller's computed externalId.
  // This avoids an immediate identity switch right after load, which can trigger
  // Freshchat's internal router error (widget.index missing param "version").
  if (preferredExternalId) {
    ensureSettings(preferredExternalId)
  }

  logDiagnostics('init:begin', {
    preferredExternalId: preferredExternalId || null,
    settingsExternalId: window.fcSettings?.externalId,
    initExternalId: window.__clsPlusFreshchatInitExternalId,
    initCalled: Boolean(window.__clsPlusFreshchatInitCalled),
    widgetPresent: Boolean(window.fcWidget),
    scriptPresent: Boolean(document.getElementById(FRESHCHAT_SCRIPT_ID)),
    didStartupCleanup,
  })

  initPromise = (async () => {
    try {
      const token = getToken()
      if (!token) {
        // If the widget is already present (e.g. injected by host page or test stubs), allow use.
        if (window.fcWidget?.user?.setProperties) {
          isWidgetInitialized = true
          window.__clsPlusFreshchatInitialized = true
        }
        initPromise = null
        return
      }

      const bootstrapExternalId =
        preferredExternalId || (window.fcSettings?.externalId as string | undefined) || getOrCreateAnonId()
      const bootstrapExternalIdString = String(bootstrapExternalId)
      ensureSettings(bootstrapExternalIdString)
      window.__clsPlusFreshchatInitExternalId = bootstrapExternalIdString

      logDiagnostics('init:bootstrap', {
        bootstrapExternalId: bootstrapExternalIdString,
        preferredExternalId: preferredExternalId || null,
        settingsExternalId: window.fcSettings?.externalId,
      })

      await ensureFreshchatLoaded()

      // Prefer the widget's auto-bootstrap (many versions initialize themselves
      // from window.fcSettings). However, some builds require calling init() at
      // least once to populate internal router params (e.g. "version") and to
      // properly honor externalId.
      await waitForWidget(15000)

      const autoReadyWidget = await waitForUserSurfaceOrNull(400)
      if (!autoReadyWidget) {
        const fcWidget = getWidget()
        const init = fcWidget?.init
        if (typeof init === 'function' && !window.__clsPlusFreshchatInitCalled && window.fcSettings) {
          window.__clsPlusFreshchatInitCalled = true
          // Call init exactly once per page-load. We still avoid calling init()
          // repeatedly, which is correlated with router crashes.
          logDiagnostics('init:call', {
            settingsExternalId: window.fcSettings.externalId,
          })
          await safeCall(() => init(window.fcSettings))
        }
      }

      await waitForUserSurface(15000)

      isWidgetInitialized = true
      window.__clsPlusFreshchatInitialized = true
    } catch (err) {
      isWidgetInitialized = false
      initPromise = null
      throw err
    }
  })()

  return initPromise
}

export const freshchatDestroy = async () => {
  const fcWidget = getWidget()
  const destroy = fcWidget?.destroy
  if (!destroy) return
  await enqueueUserOp(() => safeCall(() => destroy()))
  isWidgetInitialized = false
  initPromise = null
  lastAppliedExternalId = null
  lastAppliedProfileFieldsSignature = null
  window.__clsPlusFreshchatInitialized = false
  window.__clsPlusFreshchatInitExternalId = null
  window.__clsPlusFreshchatInitCalled = false
}

export const freshchatCloseWidget = async () => {
  // Do not initialize Freshchat just to close it.
  if (!window.fcWidget) return
  await waitForWidget(5000)
  const fcWidget = getWidget()
  const close = fcWidget?.close
  if (!close) return

  // Closing the widget before clear/re-init avoids a Freshchat race where the open conversation
  // UI rehydrates user state while logout is in progress.
  await enqueueUserOp(async () => {
    await safeCall(() => close())
    // Even if we can't observe open/close state, give the widget time to settle.
    await sleep(350)
    await waitForWidgetClosed(2000)
  })
}

export const freshchatClearUser = async () => {
  if (!window.fcWidget) return
  await waitForUserSurface(5000)
  const fcWidget = getWidget()
  const clear = fcWidget?.user?.clear
  if (!clear) return
  await enqueueUserOp(() => clear())
}

export const freshchatPrepareForLogout = async () => {
  // Same cleanup semantics as auth redirect: remove in-memory + persisted state so the
  // post-logout anonymous session does not rehydrate drafts/session and crash.
  await safeCall(async () => {
    await freshchatCloseWidget()
    await freshchatClearUser()
  })
  await safeCall(async () => {
    await freshchatDestroy()
  })
  await safeCall(async () => {
    await freshchatClearPersistedState()
  })
}

export const freshchatResetSession = async () => {
  // Creates a clean boundary so Freshchat starts a new session/conversation.
  // We intentionally avoid destroy()/re-init() due to observed router errors.
  await safeCall(async () => {
    await freshchatCloseWidget()
    await freshchatClearUser()
    // Give Freshchat time to fully clear before the caller sets new identity.
    await sleep(150)
  })
}

export const freshchatPrepareForAuthRedirect = async () => {
  // When leaving the app for Cognito, do a best-effort cleanup. If the user has
  // typed a draft message in the anon widget, persisted state can crash the
  // widget on the post-login return.
  await safeCall(async () => {
    await freshchatCloseWidget()
    await freshchatClearUser()
  })
  await safeCall(async () => {
    await freshchatDestroy()
  })
  await freshchatClearPersistedState()
}

export const freshchatGetUser = async (): Promise<unknown | undefined> => {
  if (!window.fcWidget) return undefined
  if (!isWidgetInitialized && !window.__clsPlusFreshchatInitialized) return undefined
  await waitForUserSurface(15000)
  const fcWidget = getWidget()
  const getUser = fcWidget?.user?.get
  if (!getUser) return undefined
  try {
    return await getUser()
  } catch {
    return undefined
  }
}

export const freshchatSetIdentity = async (args: {
  externalId: string
  email?: string
  userId?: string
  anonId?: string
  firstName?: string
  lastName?: string
  isAnonymous: boolean
  forceProfileFields?: boolean
}): Promise<boolean> => {
  await ensureFreshchatInitialized(args.externalId)
  if (!isWidgetInitialized) return false
  await waitForUserSurface(15000)
  const fcWidget = getWidget()
  if (!fcWidget) return false

  let didChangeExternalId = false

  await enqueueUserOp(async () => {
    logDiagnostics('identity:begin', {
      desiredExternalId: args.externalId,
      lastAppliedExternalId,
      initExternalId: window.__clsPlusFreshchatInitExternalId,
      initCalled: Boolean(window.__clsPlusFreshchatInitCalled),
      settingsExternalId: window.fcSettings?.externalId,
      widgetPresent: Boolean(window.fcWidget),
      widgetInitialized: Boolean(window.__clsPlusFreshchatInitialized),
    })

    // Re-calling fcWidget.init() after the widget is running has been observed to trigger
    // internal Ember router errors (missing route param "version").
    // Use setExternalId only for identity switching.
    const priorSettingsExternalId = window.fcSettings?.externalId
    if (window.fcSettings) window.fcSettings.externalId = args.externalId

    // If Freshchat already booted with the desired externalId (common on refresh),
    // calling setExternalId again can trigger widget router crashes.
    if (lastAppliedExternalId === null && window.__clsPlusFreshchatInitExternalId === args.externalId) {
      lastAppliedExternalId = args.externalId
    }

    const getUser = fcWidget.user?.get
    let couldReadExistingUser = false
    let existingExternalId: string | null = null
    if (typeof getUser === 'function') {
      try {
        const user = (await getUser()) as unknown
        if (user && typeof user === 'object') {
          const existingValue = (user as { externalId?: unknown }).externalId
          if (typeof existingValue === 'string') {
            existingExternalId = existingValue
            couldReadExistingUser = true
            if (existingValue === args.externalId) {
              lastAppliedExternalId = args.externalId
            }
          } else {
            // Some widget states return a user object but omit externalId.
            // Treat this as unreadable so we can avoid a redundant setExternalId
            // that may clear conversations on first refresh.
            couldReadExistingUser = false
          }
        }
      } catch {
        // If we can't read the user (common during some refresh states), prefer
        // not calling setExternalId, as that can trigger widget resetSession + router crashes.
        couldReadExistingUser = false
      }
    }

    if (!couldReadExistingUser) {
      const initExternalId = window.__clsPlusFreshchatInitExternalId
      const bootstrappedExternalId = typeof initExternalId === 'string' ? initExternalId : priorSettingsExternalId
      logDiagnostics('identity:bootstrappedCheck', {
        desiredExternalId: args.externalId,
        lastAppliedExternalId,
        bootstrappedExternalId,
      })
      // Only treat the bootstrapped externalId as authoritative on first load.
      // If we've previously applied an externalId (e.g. match context), we must
      // still attempt a switch when navigating back to lobby.
      if (
        lastAppliedExternalId === null &&
        typeof bootstrappedExternalId === 'string' &&
        bootstrappedExternalId === args.externalId
      ) {
        lastAppliedExternalId = args.externalId
      }
    }

    // Avoid re-identifying when only display fields change (e.g. match title).
    // Repeated setExternalId calls while the user is typing/sending messages can
    // destabilize the widget.
    if (lastAppliedExternalId !== args.externalId) {
      logDiagnostics('identity:setExternalId', {
        from: lastAppliedExternalId,
        to: args.externalId,
        existingExternalId,
        couldReadExistingUser,
        settingsExternalId: window.fcSettings?.externalId,
        initExternalId: window.__clsPlusFreshchatInitExternalId,
        initCalled: Boolean(window.__clsPlusFreshchatInitCalled),
      })
      const setExternalId = fcWidget.user?.setExternalId || fcWidget.setExternalId
      if (typeof setExternalId === 'function') {
        await safeCall(() => setExternalId(args.externalId))
      }

      // Give Freshchat a moment to switch identity before setting properties.
      // Keep this short; we also poll user.get() for the expected externalId.
      await sleep(100)
      await waitForExternalId(args.externalId, 800)
      lastAppliedExternalId = args.externalId
      didChangeExternalId = true
    }

    logDiagnostics('identity:end', {
      desiredExternalId: args.externalId,
      didChangeExternalId,
      lastAppliedExternalId,
      existingExternalId,
      couldReadExistingUser,
      initExternalId: window.__clsPlusFreshchatInitExternalId,
      initCalled: Boolean(window.__clsPlusFreshchatInitCalled),
      settingsExternalId: window.fcSettings?.externalId,
    })

    // Avoid touching other identity fields unless we actually switched externalId.
    // On first refresh after login, these calls can trigger Freshchat's internal
    // identifyUser/resetSession flow and wipe the conversation.
    // Exception: for anonymous users, we still want the Admin-visible name/fields
    // to be our stable CLS labels even when we skip setExternalId on bootstrap.
    const profileFieldsSignature = `${args.externalId}|${args.lastName || ''}`
    const isStableSameUser = couldReadExistingUser && existingExternalId === args.externalId
    const isBootstrappedSameUser =
      typeof window.__clsPlusFreshchatInitExternalId === 'string' &&
      window.__clsPlusFreshchatInitExternalId === args.externalId &&
      lastAppliedExternalId === args.externalId

    const shouldSetProfileFields =
      Boolean(args.forceProfileFields) ||
      didChangeExternalId ||
      // Anonymous: always apply once per (externalId,lastName) so Admin shows our CLS label.
      (args.isAnonymous && lastAppliedProfileFieldsSignature !== profileFieldsSignature) ||
      // Authenticated: apply when we can confirm identity is already correct.
      // This lets match label upgrades update the name without identity churn.
      (!args.isAnonymous &&
        (isStableSameUser || isBootstrappedSameUser) &&
        lastAppliedProfileFieldsSignature !== profileFieldsSignature)

    if (shouldSetProfileFields) {
      // Prefer explicit setters when available; some Freshchat UIs treat these as
      // the canonical contact fields (and may not reflect setProperties updates).
      const setFirstName = fcWidget.user?.setFirstName
      const firstName = args.firstName
      if (typeof setFirstName === 'function' && firstName) {
        await safeCall(() => setFirstName(firstName))
      }

      const setLastName = fcWidget.user?.setLastName
      const lastName = args.lastName
      if (typeof setLastName === 'function' && lastName) {
        await safeCall(() => setLastName(lastName))
      }

      const setEmail = fcWidget.user?.setEmail
      const email = args.email
      if (typeof setEmail === 'function' && email) {
        await safeCall(() => setEmail(email))
      }

      const props: AnyRecord = {
        isAnonymous: args.isAnonymous,
        Chattype: 'CLSPScoutSupport',
      }

      if (args.email) props.email = args.email
      if (args.userId) props.userId = args.userId
      if (args.anonId) props.anonId = args.anonId
      if (args.firstName) props.firstName = args.firstName
      if (args.lastName) props.lastName = args.lastName

      // Neo compatibility: field name used by Support tooling
      if (args.email) props.Username = args.email
      if (!args.email && args.anonId) props.Username = args.anonId

      const setProperties = fcWidget.user?.setProperties
      if (setProperties) {
        await safeCall(() => setProperties(props))
      }

      lastAppliedProfileFieldsSignature = profileFieldsSignature
    }
  })

  return didChangeExternalId
}

export const freshchatSetContextProperties = async (props: {
  matchId?: string
  mode?: string
  lastMatchId?: string
  lastMode?: string
  match?: string
}) => {
  await ensureFreshchatInitialized()
  if (!isWidgetInitialized) return
  await waitForUserSurface(15000)
  const fcWidget = getWidget()
  const setProperties = fcWidget?.user?.setProperties
  if (!setProperties) return

  const normalized: AnyRecord = {}
  if (props.matchId !== undefined) {
    normalized.matchId = props.matchId
    normalized.MatchID = props.matchId
  }
  if (props.mode !== undefined) {
    normalized.mode = props.mode
    normalized.Mode = props.mode
  }
  if (props.lastMatchId !== undefined) {
    normalized.lastMatchId = props.lastMatchId
  }
  if (props.lastMode !== undefined) {
    normalized.lastMode = props.lastMode
  }
  if (props.match !== undefined) {
    normalized.Match = props.match
  }

  await enqueueUserOp(() => setProperties(normalized))
}
